import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { XIcon, DoubbleCopyIcon, Vicon } from './icons'
import { CenteredModal, Backdrop, hideScrollbarStyles } from './SharedStyledComponents'
import { IssuedCode } from './types'
import isMobile from 'utils/isMobile'

const Container = styled(CenteredModal)`
  width: 560px;
  height: auto;
  max-height: 90vh;
  overflow-y: auto;
  gap: 16px;
  padding-bottom: 90px;
  ${hideScrollbarStyles}
  @media (max-width: 1081px) {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    top: 55.5%;
    overflow-y: auto;
    padding-bottom: 80px;
  }
`

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StatusBadge = styled.div<{ bg: string; borderColor: string }>`
  display: inline-flex;
  padding: 4px 14px;
  border-radius: 100px;
  background: ${p => p.bg};
  border: 1px solid ${p => p.borderColor};
  width: fit-content;
`

const CodeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  background: #fff;
  border-radius: 20px;
  border: 2.5px dashed #272727;
  padding: 28px 24px;
  width: 100%;
  box-sizing: border-box;
`

const CodeText = styled.div`
  font-size: 44px;
  font-weight: 900;
  color: #272727;
  letter-spacing: 10px;
  font-family: 'Courier New', monospace;
  direction: ltr;
  text-align: center;
  @media (max-width: 1081px) {
    font-size: 34px;
    letter-spacing: 6px;
  }
`

const CountdownBox = styled.div<{ expired: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${p => p.expired ? '#FFF0F0' : '#EEF9F1'};
  border-radius: 8px;
  padding: 8px 16px;
  border: 1px solid ${p => p.expired ? '#FF4D4D' : '#16AA40'};
`

const InstructionCard = styled.div`
  display: flex;
  flex-direction: column;
  background: #F0F8FF;
  border-radius: 12px;
  border: 1px solid #007EFF;
  padding: 16px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  direction: rtl;
`

const StickyBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: var(--Neutral-Surface-Minimal, #FFF);
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.08);
  border-radius: 0 0 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (max-width: 1081px) {
    display: none;
  }
`

const CopyButton = styled.button<{ copied: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  background: ${p => p.copied ? '#DCFCE4' : '#FFF'};
  border: 1px solid ${p => p.copied ? '#16AA40' : '#272727'};
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  font-family: inherit;
`

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  height: 4px;
  width: 100%;
  background: #16AA40;
  transform: scaleX(0);
  transform-origin: right;
  animation: fillProgress 1s ease-out forwards;
  @keyframes fillProgress {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
`

const ActionButton = styled.button<{ variant?: 'secondary' }>`
  width: 100%;
  padding: 14px 16px;
  background: ${p => p.variant === 'secondary' ? '#fff' : '#007EFF'};
  color: ${p => p.variant === 'secondary' ? '#272727' : '#fff'};
  border: ${p => p.variant === 'secondary' ? '1px solid #B0B0B0' : 'none'};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
`

const STATUS_CONFIG = {
  issued:  { label: 'הונפק',      bg: '#EEF9F1', border: '#16AA40', textColor: '#08832B' },
  expired: { label: 'פג תוקף',   bg: '#FFF0F0', border: '#FF4D4D', textColor: '#C0392B' },
  redeemed:{ label: 'מומש',       bg: '#EEF3FF', border: '#4F87F8', textColor: '#2C5FE4' },
}

interface IssuedCouponViewProps {
  issuedCode: IssuedCode
  onClose: () => void
  onContinue?: () => void
}

const IssuedCouponView: React.FC<IssuedCouponViewProps> = ({ issuedCode, onClose, onContinue }) => {
  const [countdown, setCountdown] = useState('')
  const [isExpired, setIsExpired] = useState(false)
  const [isCodeCopied, setIsCodeCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(issuedCode.code)
      setIsCodeCopied(true)
    } catch {
      // fallback: ignore
    }
  }

  useEffect(() => {
    const tick = () => {
      const now = new Date()
      const until = new Date(issuedCode.issuedUntil)
      const diff = until.getTime() - now.getTime()
      if (diff <= 0) {
        setIsExpired(true)
        setCountdown('00:00:00')
        return
      }
      setIsExpired(false)
      const h = Math.floor(diff / (1000 * 60 * 60))
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor((diff % (1000 * 60)) / 1000)
      setCountdown(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      )
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [issuedCode.issuedUntil])

  const statusKey = isExpired ? 'expired' : 'issued'
  const status = STATUS_CONFIG[statusKey]

  const expiryDate = new Date(issuedCode.issuedUntil)
  const formattedExpiry = expiryDate.toLocaleString('he-IL', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <>
      <Backdrop onClick={onClose} />
      <Container>
        <TitleRow>
          <Text style={{ fontSize: isMobile() ? '20px' : '22px' }} weight="bold" textcolor="#272727">
            הקוד שלך
          </Text>
          <div style={{ cursor: 'pointer' }} onClick={onClose}>
            <XIcon />
          </div>
        </TitleRow>

        {issuedCode.couponTitle && (
          <Text size="xl" weight="normal" textcolor="#5D5D5D">
            {issuedCode.couponTitle}
          </Text>
        )}

        <StatusBadge bg={status.bg} borderColor={status.border}>
          <Text size="sm" weight="bold" textcolor={status.textColor}>
            {status.label}
          </Text>
        </StatusBadge>

        <CodeCard>
          <Text size="sm" weight="normal" textcolor="#888888">קוד הטבה</Text>
          <CodeText>{issuedCode.code}</CodeText>
          <CopyButton copied={isCodeCopied} onClick={handleCopy}>
            {isCodeCopied ? (
              <>
                <Vicon fill="#08832B" />
                <Text size="md" weight="bold" textcolor="#16652D">הקוד הועתק!</Text>
              </>
            ) : (
              <>
                <DoubbleCopyIcon />
                <Text size="md" weight="bold" textcolor="#272727">העתק קוד</Text>
              </>
            )}
            {isCodeCopied && <ProgressBar />}
          </CopyButton>
          <CountdownBox expired={isExpired}>
            <Text size="sm" weight="bold" textcolor={isExpired ? '#C0392B' : '#08832B'}>
              {isExpired ? 'הקוד פג תוקף' : `בתוקף עוד: ${countdown}`}
            </Text>
          </CountdownBox>
          {!isExpired && (
            <Text size="xs" weight="normal" textcolor="#888888">
              תוקף הקוד עד {formattedExpiry}
            </Text>
          )}
        </CodeCard>

        <InstructionCard>
          <Text size="xl" weight="bold" textcolor="#007EFF">הוראות מימוש</Text>
          <Text size="md" weight="normal" textcolor="#272727">
            1. הציגו את הקוד לנציג בית העסק
          </Text>
          <Text size="md" weight="normal" textcolor="#272727">
            2. הנציג יזין את הקוד במערכת לאישור
          </Text>
          <Text size="md" weight="normal" textcolor="#272727">
            3. ההטבה תמומש לאחר אישור הנציג
          </Text>
        </InstructionCard>

        <StickyBottom>
          {onContinue && (
            <ActionButton onClick={onContinue}>
              להמשך הרכישה
            </ActionButton>
          )}
          <ActionButton variant="secondary" onClick={onClose}>סגירה</ActionButton>
        </StickyBottom>
      </Container>
    </>
  )
}

export default IssuedCouponView
